import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ApiServiceService, NotificationService } from '@oneeducation/app-core';
import { configurationModel, DirtyComponent } from '@shared/models';
import { ADMIN_ENDPOINT } from '@shared/properties';
import { DataLossService } from '@shared/services';
import { SUPPORT_DOCUMENT_IMAGE_PATH } from '@supportdocumentmanagement/constants';
import { Editor } from 'ngx-editor';

@Component({
  selector: 'app-manage-support-document-message',
  templateUrl: './manage-support-document-message.component.html',
  styleUrls: ['./manage-support-document-message.component.scss'],
  standalone: false
})
export class ManageSupportDocumentMessageComponent
  implements OnInit, DirtyComponent
{
  private readonly apiService = inject(ApiServiceService);
  private readonly notificationService = inject(NotificationService);
  private readonly dataLossService = inject(DataLossService);

  imagePath: string = SUPPORT_DOCUMENT_IMAGE_PATH;
  isEditMode = false;
  displaySaveControls = false;
  maxCharacters = 2000;
  inputCharacters = 0;
  message = '';
  inputText = '';
  isMaxCharactersLimitReached = false;
  configuration: configurationModel;
  dataLoaded = false;
  componentDetail = {
    componentName: 'SupportDocumentMessage',
    formNameToCheckForDirtyness: 'supportDocumentForm'
  };
  editor: Editor = new Editor();
  @ViewChild('buttonAddMessage') buttonAddMessage: ElementRef;
  @ViewChild('supportDocumentForm') supportDocumentForm: NgForm;
  @ViewChild('viewMessage') viewMessage: ElementRef;
  @ViewChild('buttonEdit') buttonEdit: ElementRef;
  @ViewChild('buttonSave') buttonSave: ElementRef;

  ngOnInit() {
    this.getMessage();
    this.setFocusOnAddMessageButton();
  }

  resetToOriginal() {
    this.inputCharacters = 0;
    this.dataLoaded = false;
    this.isEditMode = false;
    this.displaySaveControls = false;
    this.ngOnInit();
  }

  // set focus on add message button
  private setFocusOnAddMessageButton() {
    setTimeout(() => {
      if (this.buttonAddMessage) {
        this.buttonAddMessage?.nativeElement?.focus();
      }
    }, 0);
  }

  // set focus on edit button
  private setFocusOnEditButton() {
    setTimeout(() => {
      if (this.buttonEdit) {
        this.buttonEdit?.nativeElement?.focus();
      }
    }, 0);
  }

  // display editor in readonly mode
  addMessage() {
    this.isEditMode = true;
    this.displaySaveControls = true;
    this.getMessage();
    this.addComponentToCheckUnsavedChanges();
  }

  // edit message in editor
  editMessage() {
    this.isEditMode = true;
    this.displaySaveControls = true;
    this.inputCharacters = this.viewMessage.nativeElement?.innerText?.length;
    this.addComponentToCheckUnsavedChanges();
  }

  // cancel message in editor
  cancelMessage() {
    if (this.dataLossService.checkForUnsavedChanges(this)) {
      this.resetToOriginal();
    }
    if (this.message) {
      this.setFocusOnEditButton();
      this.isMaxCharactersLimitReached = false;
    }
  }

  // get the length of the entered input characters in editor
  textChanged(event: any) {
    this.inputText = event.replace(/(<([^>]+)>)/gi, '');
    this.isMaxCharactersLimitReached = false;
    this.inputCharacters = this.inputText.length;
    // In the case of copy paste handle maximum input text limit
    if (this.inputText.length > this.maxCharacters) {
      this.isMaxCharactersLimitReached = true;
    }
  }
  // To check the maximum limit of input text
  keyPress(event: KeyboardEvent) {
    if (this.inputText.length > this.maxCharacters - 1) {
      // this is exceeded the max length
      event.preventDefault();
      this.isMaxCharactersLimitReached = true;
    }
  }
  // To check the maximum limit of input text in the case of copy paste
  onPaste(event: KeyboardEvent) {
    if (this.inputText.length > this.maxCharacters - 1) {
      event.preventDefault();
      this.isMaxCharactersLimitReached = true;
    }
  }
  // get message from cosmos
  getMessage() {
    this.apiService
      .get(ADMIN_ENDPOINT.GetOneCloudConfigurations, '')
      .subscribe({
        next: (x: any) => {
          this.configuration = x.record;
          this.message = this.configuration?.supportingDocumentMessage;
          // If this.message value getting null then '' is assigned to this.message.
          if (this.message == null) {
            this.message = '';
          }
          this.dataLoaded = true;
          this.setFocusOnEditButton();
          this.setFocusOnAddMessageButton();
          this.editor.commands.focus();
        },
        error: () => {
          this.notificationService.showError(
            'Something went wrong, please try again.'
          );
        }
      });
  }

  // save message in cosmos
  saveMessage() {
    if (this.inputCharacters <= this.maxCharacters) {
      let message = this.supportDocumentForm.controls.Message.value;
      if (
        this.supportDocumentForm.controls.Message.value == null ||
        this.supportDocumentForm.controls.Message.value == '<p></p>'
      ) {
        message = '';
        // To set latest value this variable is used.
        this.message = message;
      }
      this.configuration.supportingDocumentMessage = message;
      this.apiService
        .patch(
          ADMIN_ENDPOINT.UpdateOneCloudConfiguration.replace(
            '{configurationName}',
            'SupportingDocumentMessage'
          ),
          this.configuration
        )
        .subscribe({
          next: () => {
            this.notificationService.showSuccess(
              'Supporting document message saved successfully.'
            );
          },
          error: () => {
            this.notificationService.showError(
              'Something went wrong, please try again.'
            );
          }
        });

      this.isEditMode = false;
      this.displaySaveControls = false;
      this.setFocusOnEditButton();
      this.setFocusOnAddMessageButton();
    }
  }

  // Reset Data method is required in Unsaved changes template
  resetData() {
    //Reset Data method is required in Unsaved changes template
  }
  canDeactivate() {
    return this.dataLossService.isDirty();
  }

  // add component to check for unsaved changes template
  private addComponentToCheckUnsavedChanges() {
    this.supportDocumentForm.reset();
    this.supportDocumentForm.valueChanges.subscribe(() => {
      this.dataLossService.addComponentToCheckUnsavedChanges(this);
      this.editor.commands.focus();
    });
  }
}
